import cn from 'classnames';
import { useState } from 'react';
// Types
import { UserRole } from 'api/graphql-global-types';
import { GetStream_stream_store } from 'api/streams/types/GetStream';
import { GetStoreBySlug_store } from 'api/store/types/GetStoreBySlug';
import {
  GetMediaPost_getMediaPost_ImagePost_store,
  GetMediaPost_getMediaPost_VideoPost_store,
} from 'api/mediaPost/types/GetMediaPost';
// Helpers
import { getCBEnvLink } from 'helpers/routes';
// Hooks
import { useGetCurrUser } from 'hooks';
// Layouts
import StripeElements from 'layouts/StripeElements/StripeElements';
// Ui3
import Modal from 'ui3/Modal/Modal';
import Button from 'ui3/Button/Button';
import Text from 'ui3/Text/Text';
// Components
import FollowButton from 'components/FollowButton/FollowButton';
import HubSpotForm from 'components/HubspotForm/HubSpotForm';
import TipAthlete from 'components/Athlete/Tip/TipAthlete';
import TipSuccessModal from 'components/TipSuccessModal/TipSuccessModal';
// Styles
import styles from './AthleteMenu.module.scss';

type AthleteMenuProps = {
  store:
    | GetStoreBySlug_store
    | GetStream_stream_store
    | GetMediaPost_getMediaPost_VideoPost_store
    | GetMediaPost_getMediaPost_ImagePost_store;
  className?: string;
};

const AthleteMenu = ({ store, className }: AthleteMenuProps) => {
  const [isFollowed, setIsFollowed] = useState<boolean>(false);
  const [isHubspotModalOpen, setIsHubSpotModalOpen] = useState<boolean>(false);
  const [storeTipModalIsOpen, setStoreTipModalIsOpen] = useState<boolean>(
    false
  );
  const [tipSuccessModalIsOpen, setTipSuccessModalIsOpen] = useState<boolean>(
    false
  );
  const [completedTipData, setCompletedTipData] = useState<any>(null);
  const [showCancelModal, setShowCancelModal] = useState<boolean>(false);

  const { data: userData } = useGetCurrUser();
  const currentUser = userData?.me;
  const isCurrentUserFan = currentUser?.role === UserRole.Customer;
  const isStoreCustomer = store.role === UserRole.Customer;

  const openHubspotModal = () => {
    setIsHubSpotModalOpen(true);
  };

  const closeHubspotModal = () => {
    setIsHubSpotModalOpen(false);
  };

  const handleOpenStoreTipModal = () => {
    setStoreTipModalIsOpen(true);
  };

  const handleStoreTipModalToggle = () => {
    setShowCancelModal(true);
  };

  const handleConfirmCancel = () => {
    setShowCancelModal(false);
    setStoreTipModalIsOpen(false);
  };

  const handleCancelCancel = () => {
    setShowCancelModal(false);
  };

  const handleTipSuccess = (tipData: any) => {
    setStoreTipModalIsOpen(false);
    setCompletedTipData(tipData);
    setTipSuccessModalIsOpen(true);
  };

  const handleCloseTipSuccessModal = () => {
    setTipSuccessModalIsOpen(false);
    setCompletedTipData(null);
  };

  const onBookMeClick = () => {
    if (store.role === UserRole.Organization) {
      openHubspotModal();
    } else {
      window.open(
        `${getCBEnvLink()}/discovery?slug=${encodeURIComponent(
          store.slug ?? ''
        )}`,
        '_blank'
      );
    }
  };

  return (
    <div className={cn(styles.buttonsWrapper, className)}>
      <FollowButton
        className={cn(styles.actionButton, {
          [styles.textColorWhite]: isFollowed,
        })}
        store={store}
        startIcon="plus"
        unfollowText="Following"
        unfollowStartIcon="check"
        onChange={setIsFollowed}
        xs="medium"
      />
      {!isStoreCustomer && (
        <Button
          variant="secondary"
          className={cn(styles.actionButton, styles.tipMeButton)}
          onClick={handleOpenStoreTipModal}
          size="medium"
          icon="heart"
        >
          Tip
        </Button>
      )}
      {!isCurrentUserFan && (
        <Button
          variant="secondary"
          className={cn(styles.actionButton, styles.bookMeButton)}
          onClick={onBookMeClick}
          size="medium"
          icon="mail"
        >
          Book Me
        </Button>
      )}

      <Modal
        size="medium"
        open={isHubspotModalOpen}
        onClose={closeHubspotModal}
      >
        <HubSpotForm />
      </Modal>

      <Modal
        open={storeTipModalIsOpen}
        onClose={handleStoreTipModalToggle}
        size="small"
        className={styles.tipModal}
        showCloseIcon={false}
        blockScroll={false}
        containerClassName={styles.tipModalContainer}
      >
        <div className={styles.tipModalContent}>
          <div className={styles.tipModalHeader}>
            <Text variant="h6" className={styles.tipModalHeaderTitle}>
              Tip {store.storeDetails?.storeName}
            </Text>
            <Button
              variant="secondary"
              subVariant="text"
              size="small"
              icon="close"
              onClick={handleStoreTipModalToggle}
              aria-label="Close"
              className={styles.tipModalCloseButton}
            />
          </div>
          <div className={styles.tipModalScrollableContent}>
            <StripeElements>
              <TipAthlete store={store} onTipSuccess={handleTipSuccess} />
            </StripeElements>
          </div>
        </div>
      </Modal>

      <TipSuccessModal
        isOpen={tipSuccessModalIsOpen}
        onClose={handleCloseTipSuccessModal}
        tipData={completedTipData}
        store={store}
      />

      <Modal
        open={showCancelModal}
        onClose={handleCancelCancel}
        size="small"
        showCloseIcon={false}
      >
        <div className={styles.cancelModal}>
          <Text variant="h3" textAlign="center" className={styles.cancelTitle}>
            Cancel Tip?
          </Text>

          <Text
            variant="body1Regular16"
            textAlign="center"
            color="light-white"
            className={styles.cancelDescription}
          >
            Are you sure you want to cancel this?
          </Text>

          <div className={styles.cancelButtons}>
            <Button
              type="button"
              variant="error"
              onClick={handleConfirmCancel}
              className={styles.cancelConfirmButton}
            >
              Yes, Cancel
            </Button>

            <Button
              type="button"
              variant="secondary"
              onClick={handleCancelCancel}
              className={styles.cancelBackButton}
            >
              Go Back
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AthleteMenu;
